import { useEffect, useRef, useCallback } from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Link2,
  Heading2,
  Heading3,
  Undo2,
  Redo2,
  Image as ImageIcon,
  Video,
  AlignLeft,
} from "lucide-react";

const TOOLBAR_BTN =
  "p-2 rounded-md hover:bg-[#1E2845] text-[#C5CAD8] hover:text-white disabled:opacity-40 transition";

/** Firefox filters video/* with a fixed extension list — list extensions explicitly. */
const VIDEO_ACCEPT = ".mp4,.webm,.mov,.m4v,.avi";
const IMAGE_ACCEPT = ".jpg,.jpeg,.png,.webp,.gif";

const VIDEO_EXT = /\.(mp4|webm|mov|m4v|avi)$/i;

function ToolbarButton({ onClick, title, children, disabled, testId }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={TOOLBAR_BTN}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/** Keep stored HTML portable — use API-relative paths, not localhost absolute URLs. */
function embedSrc(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) {
    const m = url.match(/\/api\/cms\/blog\/media\/[^?#]+/i);
    if (m) return m[0];
  }
  return url.startsWith("/") ? url : `/${url}`;
}

function insertNodeAtCursor(node, editorEl) {
  if (!editorEl) return;
  editorEl.focus();
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    if (!editorEl.contains(range.commonAncestorContainer)) {
      range.selectNodeContents(editorEl);
      range.collapse(false);
    }
    range.deleteContents();
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    return;
  }
  editorEl.appendChild(node);
}

function appendParagraphBreak(editorEl) {
  const p = document.createElement("p");
  p.appendChild(document.createElement("br"));
  editorEl.appendChild(p);
}

/**
 * Rich HTML editor for blog body (bold, lists, headings, images, videos).
 */
export default function BlogRichEditor({
  value,
  onChange,
  onUploadFile,
  onUploadError,
  uploading = false,
  placeholder = "Write your article…",
}) {
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const videoAnyInputRef = useRef(null);
  const syncingRef = useRef(false);

  const syncFromEditor = useCallback(() => {
    if (!editorRef.current || syncingRef.current) return;
    const html = editorRef.current.innerHTML;
    const normalized = html === "<br>" || html === "<div><br></div>" ? "" : html;
    onChange(normalized);
  }, [onChange]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || syncingRef.current) return;
    const current = el.innerHTML;
    const next = value || "";
    if (current !== next) {
      syncingRef.current = true;
      el.innerHTML = next || "";
      el.querySelectorAll("figure.blog-video-embed video").forEach((v) => {
        try {
          v.load();
        } catch {
          /* ignore */
        }
      });
      syncingRef.current = false;
    }
  }, [value]);

  function exec(cmd, val = null) {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    syncFromEditor();
  }

  function insertImageEmbed(src) {
    const el = editorRef.current;
    if (!el) return;
    el.focus();

    const figure = document.createElement("figure");
    figure.className = "blog-image-embed";
    figure.setAttribute("contenteditable", "false");

    const img = document.createElement("img");
    img.src = src;
    img.alt = "";
    figure.appendChild(img);

    insertNodeAtCursor(figure, el);
    appendParagraphBreak(el);
    syncFromEditor();
  }

  function insertVideoEmbed(src, label) {
    const el = editorRef.current;
    if (!el) return;
    el.focus();

    const figure = document.createElement("figure");
    figure.className = "blog-video-embed";
    figure.setAttribute("contenteditable", "false");
    if (label) figure.setAttribute("data-filename", label);

    const video = document.createElement("video");
    video.controls = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.src = src;
    figure.appendChild(video);

    insertNodeAtCursor(figure, el);
    appendParagraphBreak(el);

    video.addEventListener("loadedmetadata", () => syncFromEditor(), { once: true });
    video.addEventListener("error", () => syncFromEditor(), { once: true });
    syncFromEditor();
  }

  function insertLink() {
    const url = window.prompt("Link URL (https://…)");
    if (!url?.trim()) return;
    exec("createLink", url.trim());
  }

  function validateVideoFile(file) {
    if (!file) return "No file selected.";
    const name = file.name || "";
    if (!VIDEO_EXT.test(name)) {
      return "Choose an MP4, WebM, or MOV file.";
    }
    if (file.size > 100 * 1024 * 1024) {
      return "Video must be 100MB or smaller.";
    }
    return null;
  }

  async function processFile(file, kind) {
    if (!file || !onUploadFile) return;

    if (kind === "video") {
      const validationErr = validateVideoFile(file);
      if (validationErr) {
        onUploadError?.(new Error(validationErr));
        return;
      }
    }

    try {
      const { url, media_type } = await onUploadFile(file);
      const src = embedSrc(url);
      const isVideo =
        media_type === "video" ||
        kind === "video" ||
        VIDEO_EXT.test(file.name || "");

      if (isVideo) {
        insertVideoEmbed(src, file.name || "video");
      } else {
        insertImageEmbed(src);
      }
    } catch (err) {
      onUploadError?.(err);
    }
  }

  function onVideoInputChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) processFile(file, "video");
  }

  return (
    <div className="border border-[#1E2845] rounded-lg overflow-hidden bg-[#0A0F1F]" data-testid="blog-rich-editor">
      <input
        ref={imageInputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) processFile(file, "image");
        }}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept={VIDEO_ACCEPT}
        className="hidden"
        onChange={onVideoInputChange}
      />
      <input
        ref={videoAnyInputRef}
        type="file"
        className="hidden"
        onChange={onVideoInputChange}
      />

      <div
        className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-[#1E2845] bg-[#0F1626]"
        role="toolbar"
        aria-label="Formatting"
      >
        <ToolbarButton onClick={() => exec("bold")} title="Bold" testId="blog-fmt-bold">
          <Bold size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("italic")} title="Italic" testId="blog-fmt-italic">
          <Italic size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("underline")} title="Underline" testId="blog-fmt-underline">
          <Underline size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("strikeThrough")} title="Strikethrough" testId="blog-fmt-strike">
          <Strikethrough size={15} />
        </ToolbarButton>

        <span className="w-px h-5 bg-[#1E2845] mx-1" />

        <ToolbarButton onClick={() => exec("formatBlock", "h2")} title="Heading 2" testId="blog-fmt-h2">
          <Heading2 size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("formatBlock", "h3")} title="Heading 3" testId="blog-fmt-h3">
          <Heading3 size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("formatBlock", "p")} title="Paragraph" testId="blog-fmt-paragraph">
          <AlignLeft size={15} />
        </ToolbarButton>

        <span className="w-px h-5 bg-[#1E2845] mx-1" />

        <ToolbarButton onClick={() => exec("insertUnorderedList")} title="Bullet list" testId="blog-fmt-ul">
          <List size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("insertOrderedList")} title="Numbered list" testId="blog-fmt-ol">
          <ListOrdered size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("formatBlock", "blockquote")} title="Quote" testId="blog-fmt-quote">
          <Quote size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={insertLink} title="Insert link" testId="blog-fmt-link">
          <Link2 size={15} />
        </ToolbarButton>

        <span className="w-px h-5 bg-[#1E2845] mx-1" />

        <ToolbarButton onClick={() => exec("undo")} title="Undo" testId="blog-fmt-undo">
          <Undo2 size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("redo")} title="Redo" testId="blog-fmt-redo">
          <Redo2 size={15} />
        </ToolbarButton>

        <span className="w-px h-5 bg-[#1E2845] mx-1" />

        <ToolbarButton
          onClick={() => imageInputRef.current?.click()}
          title="Insert image (JPEG, PNG, WebP, GIF)"
          disabled={uploading}
          testId="blog-fmt-image"
        >
          <ImageIcon size={15} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => videoInputRef.current?.click()}
          title="Insert video (MP4, WebM, MOV — max 100MB)"
          disabled={uploading}
          testId="blog-fmt-video"
        >
          <Video size={15} />
        </ToolbarButton>
      </div>

      <p className="px-3 py-1.5 text-[10px] text-[#8892A8] border-b border-[#1E2845]/60">
        Media: images up to 8MB · videos MP4, WebM, MOV up to 100MB
        {uploading ? " · Uploading…" : ""}
        {" · "}
        <button
          type="button"
          className="underline hover:text-[#C5CAD8] disabled:opacity-50"
          disabled={uploading}
          onClick={() => videoAnyInputRef.current?.click()}
        >
          MP4 not listed? Pick from all files
        </button>
      </p>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        className="blog-rich-editor-area min-h-[260px] max-h-[420px] overflow-y-auto px-4 py-3 text-[14px] leading-[1.7] text-[#E5E9F2] outline-none"
        onInput={syncFromEditor}
        onBlur={syncFromEditor}
        data-testid="blog-field-body"
      />
    </div>
  );
}
